import { Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import * as d3 from 'd3';
import { EmdChartBase } from '../emd-chart-base';
import { EmdTooltipRow } from '../emd-chart-tooltip.component';
import {
  EmdPeriod,
  createZoomBrush,
  emdDateTickFormat,
  emdSafeId,
  emdWrapText,
} from '../emd-chart-utils';

export interface EmdBarDatum { period: string; value: number; }
export interface EmdLineDatum { period: string; series: string; value: number; }

interface LegendEntry { key: string; label: string; color: string; type: 'bar' | 'line'; }

/**
 * Bars on the right axis, lines on the left.
 *
 * The axis assignment is inherited from the design this replaced and both
 * consuming tabs assume it — bars carry quantity/DOH, lines carry the value
 * series.
 */
@Component({
    selector: 'emd-bar-line-trend-chart',
    templateUrl: './emd-bar-line-trend-chart.component.html',
    styleUrls: ['./emd-bar-line-trend-chart.component.scss'],
    standalone: false
})
export class EmdBarLineTrendChartComponent extends EmdChartBase {
  @Input() barData: EmdBarDatum[] = [];
  @Input() barLabel = '';
  @Input() barColor = '#1363DF';
  @Input() lineData: EmdLineDatum[] = [];
  @Input() lineColors: Record<string, string> = {};
  @Input() leftAxisLabel = '';
  /** Right (bar) axis caption, e.g. 'TOTAL SALES' / 'DOH'. */
  @Input() rightAxisLabel = '';
  @Input() dateFormat: EmdPeriod = 'W';
  @Input() chartHeight = 468;
  /** Effective period selection (raw ISO period keys); selected bars stay solid. */
  @Input() selectedPeriods: string[] = [];

  /** Bar click stages a period selection (Qlik-style). Emits the raw ISO key. */
  @Output() barClick = new EventEmitter<{ period: string; ctrlKey: boolean }>();

  @ViewChild('chart', { static: true }) chartRef!: ElementRef<HTMLDivElement>;

  tooltipVisible = false;
  tooltipTitle = '';
  tooltipRows: EmdTooltipRow[] = [];
  tooltipX = 0;
  tooltipY = 0;

  legend: LegendEntry[] = [];
  hidden = new Set<string>();

  private visibleDomain: [Date, Date] | null = null;
  private allDates: Date[] = [];
  private focusIndex = -1;
  /** Bar thickness for the current render; set in render(), drawn in drawMain(). */
  private barWidth = 0;

  // Draggable y-axis range clamp (the green/grey corner handles). `undefined`
  // means auto (full data extent). Data-driven max of each axis for the current
  // draw, so the handles know their travel range.
  private barMin?: number;
  private barMax?: number;
  private lineMin?: number;
  private lineMax?: number;
  private barAutoMax = 1;
  private lineAutoMax = 1;

  // Geometry follows the original combo-bar-card: 90px side margins with the
  // y axes drawn a 40px gutter OUTSIDE the plot, so edge-period bars (centred
  // on the scale ends, overhanging by at most barWidth/2 = gutter - 5 - half)
  // never reach the axis text. The top pad holds the wrapped axis captions
  // above the tallest bar's value label; the original's 100px top band is not
  // needed because the legend is HTML above the SVG here.
  private readonly margin = { top: 48, right: 90, bottom: 22, left: 90 };
  /** How far the y axes sit outside the plot (original: margins.axis = 40). */
  private readonly axisGutter = 40;
  private readonly brushHeight = 52;
  private readonly brushGap = 60;

  constructor(host: ElementRef<HTMLElement>) {
    super(host);
  }

  /**
   * A brushed zoom window only means anything for the data it was brushed on.
   * New data (e.g. the Summary tab re-bucketing weeks into months or years)
   * moves the date span, and a kept window silently clips the new series —
   * fatal when the new data is too short to show a brush that would reset it.
   * A pure container resize keeps the window, so this cannot live in render().
   */
  override ngOnChanges(changes: SimpleChanges): void {
    if (changes['barData'] || changes['lineData'] || changes['dateFormat']) {
      this.visibleDomain = null;
      // New data moves the y extents, so a stale manual clamp would misbehave.
      this.barMin = this.barMax = this.lineMin = this.lineMax = undefined;
    }
    super.ngOnChanges(changes);
  }

  get hasData(): boolean {
    return this.barData.length > 0 || this.lineData.length > 0;
  }

  /** The strip is unusable on a narrow screen and the axis reads fine without it. */
  get showBrush(): boolean {
    return this.width >= 640 && this.allDates.length > 2;
  }

  get ariaLabel(): string {
    if (!this.hasData) { return 'Trend chart, no data'; }
    const series = this.legend.map((l) => l.label).join(', ');
    return `Trend chart over ${this.allDates.length} periods. Series: ${series}.`;
  }

  toggleSeries(key: string): void {
    if (this.hidden.has(key)) {
      this.hidden.delete(key);
    } else {
      // Never hide the last visible series — an empty chart reads as broken.
      if (this.legend.length - this.hidden.size <= 1) { return; }
      this.hidden.add(key);
    }
    this.render();
  }

  /** Hover preview from the legend; dims everything but the hovered entry. */
  emphasise(key: string | null): void {
    const svg = d3.select(this.chartRef.nativeElement).select('svg');

    svg.selectAll<SVGPathElement, any>('.emd-line').attr('opacity', function () {
      return !key || this.getAttribute('data-key') === key ? 1 : 0.15;
    });
    svg.selectAll<SVGCircleElement, any>('.emd-dot').attr('opacity', function () {
      return !key || this.getAttribute('data-key') === key ? 1 : 0.15;
    });
    svg.selectAll<SVGRectElement, any>('.emd-bar').attr(
      'opacity',
      !key || key === '__bar__' ? 1 : 0.15
    );
  }

  protected render(): void {
    const el = this.chartRef?.nativeElement;
    if (!el || !this.width) { return; }

    d3.select(el).selectAll('svg').remove();
    this.buildLegend();

    if (!this.hasData) { return; }

    const parse = (p: string) => new Date(p);
    this.allDates = Array.from(
      new Set([...this.barData, ...this.lineData].map((d) => d.period))
    )
      .map(parse)
      .sort((a, b) => a.getTime() - b.getTime());

    if (!this.allDates.length) { return; }

    const chartHeight = this.effectiveHeight(this.chartHeight);
    const brushSpace = this.showBrush ? this.brushHeight + this.brushGap : 0;
    const innerWidth = Math.max(10, this.width - this.margin.left - this.margin.right);
    const innerHeight = Math.max(
      60,
      chartHeight - this.margin.top - this.margin.bottom - brushSpace
    );

    const svg = d3
      .select(el)
      .append('svg')
      .attr('width', this.width)
      .attr('height', chartHeight)
      .attr('role', 'presentation');

    const main = svg
      .append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

    const xAll = d3
      .scaleTime()
      .domain(d3.extent(this.allDates) as [Date, Date])
      .range([0, innerWidth]);

    const domain = this.visibleDomain ?? (d3.extent(this.allDates) as [Date, Date]);

    // Full-width scale exactly like the original: edge-period bars overhang the
    // plot into the axis gutter, and the width cap (gutter - 5) guarantees the
    // overhang never reaches the out-set axes. Width comes from the FULL
    // dataset so bars do not resize as the brush window changes the count.
    this.barWidth = Math.min(
      this.axisGutter - 5,
      innerWidth / (Math.max(this.barData.length - 1, 1) * 2)
    );
    const x = d3.scaleTime().domain(domain).range([0, innerWidth]);

    // Persistent interaction + handle layers, ABOVE `main`. drawMain() wipes
    // `main` on every brush/clamp redraw, so anything that must survive those
    // redraws (the hover overlay, the crosshair, the y-axis handles) lives here
    // instead — otherwise brushing would silently kill hover and bar clicks.
    const overlayLayer = svg
      .append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);
    const handlesLayer = svg
      .append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

    const redraw = () => {
      this.drawMain(main, x, innerWidth, innerHeight);
      this.positionAxisHandles(handlesLayer, innerHeight);
    };
    this.drawMain(main, x, innerWidth, innerHeight);

    if (this.showBrush) {
      const brushGroup = svg
        .append('g')
        .attr(
          'transform',
          `translate(${this.margin.left},${this.margin.top + innerHeight + this.brushGap})`
        );

      createZoomBrush({
        group: brushGroup,
        width: innerWidth,
        height: this.brushHeight,
        xScaleAll: xAll,
        dates: this.allDates,
        drawPreview: (g, scale, height) => this.drawPreview(g, scale, height, innerWidth),
        onBrush: (next) => {
          this.visibleDomain = next;
          x.domain(next);
          redraw();
        },
      });
    }

    this.attachPointer(svg, overlayLayer, x, innerWidth, innerHeight);
    this.drawAxisHandles(handlesLayer, innerWidth, innerHeight, redraw);
  }

  private visibleLines(): EmdLineDatum[] {
    return this.lineData.filter((d) => !this.hidden.has(emdSafeId(d.series)));
  }

  /** Every Nth in-window period, with N chosen so ~70px labels never collide. */
  private periodTickValues(from: Date, to: Date): Date[] {
    const visible = this.allDates.filter((d) => d >= from && d <= to);
    const stride = Math.max(1, Math.ceil(visible.length / this.tickCount(140, 8)));
    return visible.filter((_, i) => i % stride === 0);
  }

  private barsHidden(): boolean {
    return this.hidden.has('__bar__');
  }

  private drawMain(
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    x: d3.ScaleTime<number, number>,
    innerWidth: number,
    innerHeight: number
  ): void {
    g.selectAll('*').remove();

    const [from, to] = x.domain();
    const inWindow = (p: string) => {
      const d = new Date(p);
      return d >= from && d <= to;
    };

    const bars = this.barsHidden() ? [] : this.barData.filter((d) => inWindow(d.period));
    const lines = this.visibleLines().filter((d) => inWindow(d.period));

    // Data-derived extents (no .nice()/headroom, as in the original), unless the
    // user has dragged a y-axis handle to clamp the range. autoMax is kept so
    // the handles know their travel; it stays the FULL bar/line max (not the
    // clamped one) so a clamp can always be dragged back open.
    this.lineAutoMax = d3.max(lines, (d) => d.value) ?? 1;
    this.barAutoMax = d3.max(bars, (d) => d.value) ?? 1;

    const yLeft = d3
      .scaleLinear()
      .domain([this.lineMin ?? 0, this.lineMax ?? this.lineAutoMax])
      .range([innerHeight, 0]);

    const yRight = d3
      .scaleLinear()
      .domain([this.barMin ?? 0, this.barMax ?? this.barAutoMax])
      .range([innerHeight, 0]);

    // Gridlines: solid pale rules, three levels like the original, spanning
    // axis to axis so bars overhanging the plot edges still sit on a rule.
    g.append('g')
      .attr('class', 'emd-grid')
      .call(
        d3
          .axisLeft(yLeft)
          .ticks(3)
          .tickSize(-innerWidth)
          .tickFormat(() => '')
      )
      .call((sel) => {
        sel.select('.domain').remove();
        sel
          .selectAll('line')
          .attr('x1', -this.axisGutter)
          .attr('x2', innerWidth + this.axisGutter);
      });

    g.append('g')
      .attr('class', 'emd-axis emd-axis--x')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        d3
          .axisBottom(x)
          // Ticks anchored to the actual data periods (like the original's
          // under-bar label strip), thinned to a stride the labels fit at —
          // d3's own time ticks jump from weekly (overlapping) straight to
          // monthly (three labels), since it has no fortnight interval.
          .tickValues(this.periodTickValues(from, to))
          .tickSize(0)
          .tickPadding(8)
          .tickFormat(emdDateTickFormat(this.dateFormat) as any)
      );

    // Y axes a full gutter outside the plot, as in the original — this is what
    // keeps overhanging edge bars clear of the tick labels.
    g.append('g')
      .attr('class', 'emd-axis')
      .attr('transform', `translate(${-this.axisGutter},0)`)
      .call(d3.axisLeft(yLeft).ticks(3).tickSize(0).tickPadding(6).tickFormat((v) => (+v === 0 ? '' : d3.format('-2~s')(+v))));

    g.append('g')
      .attr('class', 'emd-axis')
      .attr('transform', `translate(${innerWidth + this.axisGutter},0)`)
      .call(d3.axisRight(yRight).ticks(3).tickSize(0).tickPadding(6).tickFormat((v) => (+v === 0 ? '' : d3.format('-2~s')(+v))));

    // Captions wrapped directly above each out-set axis, matching the
    // original's 8px grey labels (left one right-aligned, right one left-aligned).
    if (this.leftAxisLabel) {
      g.append('text')
        .attr('class', 'emd-axis-label')
        .attr('transform', `translate(${-this.axisGutter},-34)`)
        .attr('text-anchor', 'end')
        .attr('font-size', 8)
        .text(this.leftAxisLabel)
        .call(emdWrapText, 40);
    }
    if (this.rightAxisLabel) {
      g.append('text')
        .attr('class', 'emd-axis-label')
        .attr('transform', `translate(${innerWidth + this.axisGutter},-34)`)
        .attr('text-anchor', 'start')
        .attr('font-size', 8)
        .text(this.rightAxisLabel)
        .call(emdWrapText, 40);
    }

    // Computed in render() alongside the scale inset, from the FULL dataset.
    const barWidth = this.barWidth;

    // Domed (semicircular) top, matching the original bar path, with a 5px floor.
    const barPath = (h: number): string => {
      const barHeight = Math.max(h, h > 0 ? 5 : 0);
      const curve = barWidth / 2;
      return `M0,${barHeight}L0,${curve} Q0,0 ${barWidth / 2},0 Q${barWidth},0 ${barWidth},${curve} L${barWidth},${barHeight}Z`;
    };

    const anySel = this.selectedPeriods.length > 0;
    // Raw ISO keys, not formatted labels — labels change with the bucket and
    // once collided between buckets ("Dec 2025" for both M and Q).
    const isSel = (d: EmdBarDatum) => this.selectedPeriods.indexOf(d.period) > -1;

    // Bars/lines/dots draw into a plot-clipped subgroup so a clamped y-axis (a
    // max handle dragged below the data) keeps taller bars inside the plot
    // instead of overshooting into the caption band. The clip still allows the
    // horizontal gutter overhang of the edge bars. Axes/grid/captions stay in
    // `g`, unclipped.
    const clipId = `${this.uid}-plot-clip`;
    g.append('clipPath')
      .attr('id', clipId)
      .append('rect')
      .attr('x', -this.axisGutter)
      .attr('y', 0)
      .attr('width', innerWidth + this.axisGutter * 2)
      .attr('height', innerHeight);
    const plot = g.append('g').attr('clip-path', `url(#${clipId})`);

    plot.selectAll('path.emd-bar')
      .data(bars)
      .enter()
      .append('path')
      .attr('class', 'emd-bar')
      .attr('fill', this.barColor)
      // Qlik-style: selected bars stay solid, the rest fade (never disappear).
      .attr('opacity', (d) => (!anySel || isSel(d) ? 1 : 0.25))
      .style('cursor', this.barClick.observers.length ? 'pointer' : 'default')
      .attr('transform', (d) => `translate(${x(new Date(d.period)) - barWidth / 2},${yRight(d.value)})`)
      .attr('d', (d) => barPath(innerHeight - yRight(d.value)))
      .on('click', (event: MouseEvent, d: EmdBarDatum) =>
        this.barClick.emit({ period: d.period, ctrlKey: event.ctrlKey || event.metaKey })
      );

    // No value labels above the bars — the app's combo-bar-card keeps the bars
    // clean and surfaces the numbers on hover, which is the look being matched.

    const grouped = d3.group(lines, (d) => d.series);
    const lineGen = d3
      .line<EmdLineDatum>()
      .curve(d3.curveMonotoneX)
      .x((d) => x(new Date(d.period)))
      .y((d) => yLeft(d.value));

    grouped.forEach((rows, series) => {
      const sorted = [...rows].sort(
        (a, b) => new Date(a.period).getTime() - new Date(b.period).getTime()
      );
      const color = this.lineColors[series] ?? '#1363DF';

      plot.append('path')
        .attr('class', 'emd-line')
        .datum(sorted)
        .attr('data-key', emdSafeId(series))
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', 3)
        .attr('d', lineGen);

      // Reference dot = a white backing circle (so the line does not show
      // through the translucent dot) under a translucent tinted dot with a
      // solid colour ring.
      plot.selectAll(`circle.emd-dot-bg-${emdSafeId(series)}`)
        .data(sorted)
        .enter()
        .append('circle')
        .attr('class', `emd-dot-bg emd-dot-bg-${emdSafeId(series)}`)
        .attr('cx', (d) => x(new Date(d.period)))
        .attr('cy', (d) => yLeft(d.value))
        .attr('r', 3)
        .attr('fill', '#fff')
        .attr('stroke', 'none');

      plot.selectAll(`circle.emd-dot-${emdSafeId(series)}`)
        .data(sorted)
        .enter()
        .append('circle')
        .attr('class', `emd-dot emd-dot-${emdSafeId(series)}`)
        .attr('data-key', emdSafeId(series))
        .attr('cx', (d) => x(new Date(d.period)))
        .attr('cy', (d) => yLeft(d.value))
        .attr('r', 3)
        .attr('fill', color)
        .attr('fill-opacity', 0.3)
        .attr('stroke', color)
        .attr('stroke-width', 2);
    });

    this.yLeft = yLeft;
    this.yRight = yRight;
  }

  private yLeft?: d3.ScaleLinear<number, number>;
  private yRight?: d3.ScaleLinear<number, number>;

  private drawPreview(
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    x: d3.ScaleTime<number, number>,
    height: number,
    innerWidth: number
  ): void {
    const bars = this.barsHidden() ? [] : this.barData;
    const lines = this.visibleLines();

    const yBar = d3
      .scaleLinear()
      .domain([0, d3.max(bars, (d) => d.value) ?? 1])
      .range([height, 2]);

    const yLine = d3
      .scaleLinear()
      .domain([0, d3.max(lines, (d) => d.value) ?? 1])
      .range([height, 2]);

    const barWidth = Math.max(1, (innerWidth / Math.max(bars.length, 1)) * 0.4);

    g.selectAll('rect')
      .data(bars)
      .enter()
      .append('rect')
      .attr('x', (d) => x(new Date(d.period)) - barWidth / 2)
      .attr('width', barWidth)
      .attr('y', (d) => yBar(d.value))
      .attr('height', (d) => Math.max(0, height - yBar(d.value)))
      .attr('fill', this.barColor)
      .attr('opacity', 0.35);

    const lineGen = d3
      .line<EmdLineDatum>()
      .curve(d3.curveMonotoneX)
      .x((d) => x(new Date(d.period)))
      .y((d) => yLine(d.value));

    d3.group(lines, (d) => d.series).forEach((rows, series) => {
      const sorted = [...rows].sort(
        (a, b) => new Date(a.period).getTime() - new Date(b.period).getTime()
      );
      g.append('path')
        .datum(sorted)
        .attr('fill', 'none')
        .attr('stroke', this.lineColors[series] ?? '#1363DF')
        .attr('stroke-width', 1.2)
        .attr('opacity', 0.5)
        .attr('d', lineGen);
    });
  }

  // ── Crosshair + unified tooltip ─────────────────────────────────────────────

  private attachPointer(
    svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
    main: d3.Selection<SVGGElement, unknown, null, undefined>,
    x: d3.ScaleTime<number, number>,
    innerWidth: number,
    innerHeight: number
  ): void {
    const crosshair = main
      .append('line')
      .attr('class', 'emd-crosshair')
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .style('opacity', 0);

    // Full-plot capture surface for hover AND click. It sits above the bars, so
    // bar clicks must be handled HERE — a per-bar handler is unreachable for any
    // bar the overlay covers (which is every bar except the first/last, whose
    // outer halves overhang the plot edges). Widened by the axis gutter so the
    // overhanging edge bars are captured too.
    const overlay = main
      .append('rect')
      .attr('x', -this.axisGutter)
      .attr('width', innerWidth + this.axisGutter * 2)
      .attr('height', innerHeight)
      .attr('fill', 'transparent')
      .style('cursor', this.barClick.observers.length ? 'pointer' : 'default');

    const showAt = (date: Date, clientX: number, clientY: number) => {
      const nearest = this.nearestDate(date, x.domain() as [Date, Date]);
      if (!nearest) { return; }

      crosshair.attr('x1', x(nearest)).attr('x2', x(nearest)).style('opacity', 1);
      // Dots live in the redraw layer (a sibling of this overlay layer), so
      // query from the svg root, not from `layer`.
      svg.selectAll('.emd-dot, .emd-dot-bg').attr('r', (d: any) =>
        new Date(d.period).getTime() === nearest.getTime() ? 4.5 : 3
      );

      this.buildTooltip(nearest);
      const rect = this.host.nativeElement.getBoundingClientRect();
      this.tooltipX = clientX - rect.left;
      this.tooltipY = clientY - rect.top;
      this.tooltipVisible = true;
    };

    overlay
      .on('mousemove', (event: MouseEvent) => {
        const [mx] = d3.pointer(event);
        showAt(x.invert(mx), event.clientX, event.clientY);
      })
      .on('mouseleave', () => {
        crosshair.style('opacity', 0);
        svg.selectAll('.emd-dot, .emd-dot-bg').attr('r', 3);
        this.tooltipVisible = false;
      })
      // Click anywhere selects the nearest period's bar — the only way to reach
      // the bars the overlay covers, and a more forgiving target than the bar itself.
      .on('click', (event: MouseEvent) => {
        if (!this.barClick.observers.length) { return; }
        const [mx] = d3.pointer(event);
        const nearest = this.nearestDate(x.invert(mx), x.domain() as [Date, Date]);
        if (!nearest) { return; }
        const key = nearest.getTime();
        const bar = this.barData.find((d) => new Date(d.period).getTime() === key);
        if (bar) {
          this.barClick.emit({ period: bar.period, ctrlKey: event.ctrlKey || event.metaKey });
        }
      });

    this.keyboardStep = (delta: number) => {
      const dates = this.datesInWindow(x.domain() as [Date, Date]);
      if (!dates.length) { return; }
      this.focusIndex = Math.max(0, Math.min(dates.length - 1, this.focusIndex + delta));
      const target = dates[this.focusIndex];
      const rect = this.host.nativeElement.getBoundingClientRect();
      showAt(
        target,
        rect.left + this.margin.left + x(target),
        rect.top + this.margin.top + innerHeight / 2
      );
    };

    this.keyboardClear = () => {
      crosshair.style('opacity', 0);
      this.tooltipVisible = false;
      this.focusIndex = -1;
    };
  }

  private keyboardStep?: (delta: number) => void;
  private keyboardClear?: () => void;

  // ── Y-axis range handles ─────────────────────────────────────────────────────
  // Green (max, top) / grey (min, bottom) circles at each axis corner, matching
  // the app combo-bar-card's range sliders. They clamp the axis's visible range;
  // dragging one sets the y-override and re-renders. They live in a persistent
  // layer (not `main`), so drawMain's redraws never wipe them mid-drag.

  private axisRefScale(axisKey: 'line' | 'bar', innerHeight: number): d3.ScaleLinear<number, number> | null {
    const autoMax = axisKey === 'line' ? this.lineAutoMax : this.barAutoMax;
    if (!(autoMax > 0)) { return null; }
    // Full-extent scale, independent of the current clamp, so a handle's pixel
    // position maps to a stable value throughout a drag.
    return d3.scaleLinear().domain([0, autoMax]).range([innerHeight, 0]);
  }

  private drawAxisHandles(
    layer: d3.Selection<SVGGElement, unknown, null, undefined>,
    innerWidth: number,
    innerHeight: number,
    redraw: () => void
  ): void {
    layer.selectAll('*').remove();

    const axes: { key: 'line' | 'bar'; axisX: number; present: boolean }[] = [
      { key: 'line', axisX: -this.axisGutter, present: this.lineData.length > 0 },
      { key: 'bar', axisX: innerWidth + this.axisGutter, present: this.barData.length > 0 && !this.barsHidden() },
    ];

    for (const axis of axes) {
      const ref = this.axisRefScale(axis.key, innerHeight);
      if (!axis.present || !ref) { continue; }
      const autoMax = axis.key === 'line' ? this.lineAutoMax : this.barAutoMax;
      const gap = autoMax * 0.08; // keep a minimum visible band between handles

      const getMin = () => (axis.key === 'line' ? this.lineMin : this.barMin) ?? 0;
      const getMax = () => (axis.key === 'line' ? this.lineMax : this.barMax) ?? autoMax;

      const make = (kind: 'max' | 'min', fill: string) => {
        layer
          .append('circle')
          .attr('class', `emd-axis-handle emd-axis-handle--${kind}`)
          .attr('data-axis', axis.key)
          .attr('data-kind', kind)
          .attr('cx', axis.axisX)
          .attr('cy', ref(kind === 'max' ? getMax() : getMin()))
          .attr('r', 5.5)
          .attr('fill', fill)
          .style('cursor', 'ns-resize')
          .call(
            d3.drag<SVGCircleElement, unknown>().on('drag', (event) => {
              let v = Math.max(0, Math.min(autoMax, ref.invert(event.y)));
              if (kind === 'max') {
                v = Math.max(getMin() + gap, v);
                if (axis.key === 'line') { this.lineMax = v; } else { this.barMax = v; }
              } else {
                v = Math.min(getMax() - gap, v);
                if (axis.key === 'line') { this.lineMin = v; } else { this.barMin = v; }
              }
              redraw();
            })
          );
      };

      make('max', '#43C678'); // green, top of the axis
      make('min', '#9AA7B8'); // grey, bottom of the axis
    }
  }

  /** Re-seat the persistent handles after a redraw (drag or brush). */
  private positionAxisHandles(
    layer: d3.Selection<SVGGElement, unknown, null, undefined>,
    innerHeight: number
  ): void {
    layer.selectAll<SVGCircleElement, unknown>('.emd-axis-handle').each((_d, i, nodes) => {
      const el = d3.select(nodes[i]);
      const axisKey = el.attr('data-axis') as 'line' | 'bar';
      const kind = el.attr('data-kind');
      const ref = this.axisRefScale(axisKey, innerHeight);
      if (!ref) { return; }
      const autoMax = axisKey === 'line' ? this.lineAutoMax : this.barAutoMax;
      const value = kind === 'max'
        ? ((axisKey === 'line' ? this.lineMax : this.barMax) ?? autoMax)
        : ((axisKey === 'line' ? this.lineMin : this.barMin) ?? 0);
      el.attr('cy', ref(value));
    });
  }

  private datesInWindow([from, to]: [Date, Date]): Date[] {
    return this.allDates.filter((d) => d >= from && d <= to);
  }

  private nearestDate(target: Date, window: [Date, Date]): Date | null {
    const candidates = this.datesInWindow(window);
    if (!candidates.length) { return null; }
    return candidates.reduce((best, d) =>
      Math.abs(d.getTime() - target.getTime()) < Math.abs(best.getTime() - target.getTime())
        ? d
        : best
    );
  }

  private buildTooltip(date: Date): void {
    const key = date.getTime();
    const index = this.allDates.findIndex((d) => d.getTime() === key);
    const prevKey = index > 0 ? this.allDates[index - 1].getTime() : null;
    const rows: EmdTooltipRow[] = [];
    const fmt = (v: number) => v.toLocaleString('en-US', { maximumFractionDigits: 2 });
    const move = (curr: number, prior?: number): 'up' | 'down' | null =>
      prior === undefined || prior === curr ? null : curr > prior ? 'up' : 'down';

    if (!this.barsHidden()) {
      const bar = this.barData.find((d) => new Date(d.period).getTime() === key);
      if (bar) {
        const prior = prevKey === null
          ? undefined
          : this.barData.find((d) => new Date(d.period).getTime() === prevKey)?.value;
        rows.push({
          label: this.barLabel || 'Total',
          value: fmt(bar.value),
          color: this.barColor,
          movement: move(bar.value, prior),
        });
      }
    }

    const lines = this.visibleLines();
    lines
      .filter((d) => new Date(d.period).getTime() === key)
      .forEach((d) => {
        const prior = prevKey === null
          ? undefined
          : lines.find((p) => p.series === d.series && new Date(p.period).getTime() === prevKey)?.value;
        rows.push({
          label: d.series,
          value: fmt(d.value),
          color: this.lineColors[d.series] ?? '#1363DF',
          movement: move(d.value, prior),
        });
      });

    // Header names the metric and the full date, matching the original.
    const metric = this.rightAxisLabel || this.leftAxisLabel || '';
    const dateStr = d3.timeFormat('%d-%b-%Y')(date);
    this.tooltipTitle = metric ? `${metric} (${dateStr})` : dateStr;
    this.tooltipRows = rows;
  }

  private buildLegend(): void {
    const entries: LegendEntry[] = [];

    if (this.barData.length && this.barLabel) {
      entries.push({ key: '__bar__', label: this.barLabel, color: this.barColor, type: 'bar' });
    }

    Array.from(new Set(this.lineData.map((d) => d.series))).forEach((series) =>
      entries.push({
        key: emdSafeId(series),
        label: series,
        color: this.lineColors[series] ?? '#1363DF',
        type: 'line',
      })
    );

    this.legend = entries;
  }

  // ── Keyboard ────────────────────────────────────────────────────────────────

  onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        this.keyboardStep?.(1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.keyboardStep?.(-1);
        break;
      case 'Escape':
        this.keyboardClear?.();
        break;
    }
  }

  onBlur(): void {
    this.keyboardClear?.();
  }
}
